import { useState, useMemo, useEffect } from 'react'
import { ChevronsUpDown, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Checkbox } from '../ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { cn } from '../../lib/utils'
import { ALL_LANGUAGES, DEFAULT_TOP_LANGUAGE_CODES, type Language } from '../../lib/constants/languages'

interface MultiLanguageSelectorProps {
  value: string[] // Array of language codes
  onChange: (value: string[]) => void // Receives array of language codes
  disabled?: boolean
  showActiveFilters?: boolean
  onClear?: () => void
  languages?: Language[] // Optional: custom languages list
  topLanguageCodes?: string[] // Optional: custom top languages (for ordering)
  applyMode?: boolean // When true, show Apply button and only update on apply/close
}

export function MultiLanguageSelector({ 
  value, 
  onChange, 
  disabled,
  showActiveFilters = false,
  onClear,
  languages = ALL_LANGUAGES,
  topLanguageCodes = DEFAULT_TOP_LANGUAGE_CODES,
  applyMode = false
}: MultiLanguageSelectorProps) {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const [tempValue, setTempValue] = useState<string[]>(value)

  // Sync tempValue when value changes externally
  useEffect(() => {
    setTempValue(value)
  }, [value])

  // Get selected languages details
  const selectedLanguages = languages.filter((lang) => value.includes(lang.code))
  
  // Get the working value based on mode
  const workingValue = applyMode ? tempValue : value

  // Organize languages
  const { topLanguages, otherLanguages } = useMemo(() => {
    const top = languages.filter((lang) => topLanguageCodes.includes(lang.code))
      .sort((a, b) => {
        // Sort by order in topLanguageCodes
        const indexA = topLanguageCodes.indexOf(a.code)
        const indexB = topLanguageCodes.indexOf(b.code)
        return indexA - indexB
      })
    const other = languages.filter((lang) => !topLanguageCodes.includes(lang.code))
      .sort((a, b) => {
        // Sort alphabetically by name
        return a.name.localeCompare(b.name)
      })

    return { topLanguages: top, otherLanguages: other }
  }, [languages, topLanguageCodes])

  const handleToggleLanguage = (code: string) => {
    if (applyMode) {
      // In apply mode, update temp value
      if (tempValue.includes(code)) {
        setTempValue(tempValue.filter(c => c !== code))
      } else {
        setTempValue([...tempValue, code])
      }
    } else {
      // Immediate mode, update value directly
      if (value.includes(code)) {
        onChange(value.filter(c => c !== code))
      } else {
        onChange([...value, code])
      }
    }
  }

  const handleSelectAll = () => {
    const allCodes = languages.map(lang => lang.code)
    if (applyMode) {
      if (tempValue.length === languages.length) {
        setTempValue([])
      } else {
        setTempValue(allCodes)
      }
    } else {
      if (value.length === languages.length) {
        onChange([])
      } else {
        onChange(allCodes)
      }
    }
  }

  const handleApply = () => {
    onChange(tempValue)
    setOpen(false)
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (applyMode && !newOpen) {
      // When closing in apply mode, apply the temp value
      onChange(tempValue)
    }
    setOpen(newOpen)
  }

  const getDisplayText = () => {
    if (value.length === 0) {
      return t('common.allLanguages')
    }
    if (value.length === 1) {
      const lang = languages.find(l => l.code === value[0])
      return lang ? `${lang.flag} ${lang.name}` : t('common.allLanguages')
    }
    return t('common.languagesSelected', { count: value.length })
  }

  return (
    <div className="multi-language-selector-wrapper flex items-center gap-2 flex-1">
      <div className="w-64">
        <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="multi-language-selector-btn w-full justify-between font-normal"
          >
            <span className="text-muted-foreground">{getDisplayText()}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="multi-language-selector-popover w-[400px] p-0" align="start">
          <div className="border-b p-2 flex items-center justify-between gap-2">
            <button
              onClick={handleSelectAll}
              className="text-left text-sm text-primary hover:bg-accent rounded px-2 py-1"
            >
              {workingValue.length === languages.length ? t('common.deselectAll') : t('common.selectAllLanguages')}
            </button>
            {applyMode && (
              <Button
                onClick={handleApply}
                size="sm"
                className="h-7"
              >
                {t('common.apply')}
              </Button>
            )}
          </div>
          <div className="max-h-[300px] overflow-y-auto p-1">
            {topLanguages.length > 0 && (
              <div>
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                  {t('common.commonLanguages')}
                </div>
                {topLanguages.map((language) => {
                  const isSelected = workingValue.includes(language.code)
                  return (
                    <div
                      key={language.code}
                      onClick={(e) => {
                        // Don't toggle if clicking directly on checkbox (it handles its own click)
                        if ((e.target as HTMLElement).closest('[role="checkbox"]')) {
                          return
                        }
                        handleToggleLanguage(language.code)
                      }}
                      className={cn(
                        'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                        isSelected && 'bg-accent'
                      )}
                    >
                      <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleToggleLanguage(language.code)}
                          className="mr-2"
                        />
                      </div>
                      <span className="mr-2">{language.flag}</span>
                      <span className="flex-1 text-left">{language.name}</span>
                      <span className="text-xs text-muted-foreground">{language.code}</span>
                    </div>
                  )
                })}
              </div>
            )}
            {otherLanguages.length > 0 && (
              <div className={topLanguages.length > 0 ? 'mt-2 border-t pt-2' : ''}>
                {topLanguages.length > 0 && (
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                    {t('common.otherLanguages')}
                  </div>
                )}
                {otherLanguages.map((language) => {
                  const isSelected = workingValue.includes(language.code)
                  return (
                    <div
                      key={language.code}
                      onClick={(e) => {
                        // Don't toggle if clicking directly on checkbox (it handles its own click)
                        if ((e.target as HTMLElement).closest('[role="checkbox"]')) {
                          return
                        }
                        handleToggleLanguage(language.code)
                      }}
                      className={cn(
                        'relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                        isSelected && 'bg-accent'
                      )}
                    >
                      <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleToggleLanguage(language.code)}
                          className="mr-2"
                        />
                      </div>
                      <span className="mr-2">{language.flag}</span>
                      <span className="flex-1 text-left">{language.name}</span>
                      <span className="text-xs text-muted-foreground">{language.code}</span>
                    </div>
                  )
                })}
              </div>
            )}
            {topLanguages.length === 0 && otherLanguages.length === 0 && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {t('common.noLanguagesFound')}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
      </div>

      {/* Active Filters - Inline to the right of dropdown */}
      {showActiveFilters && (
        <div className="multi-language-active-filters flex items-center gap-2 flex-wrap">
          {value.length > 0 && (
            <>
              <span className="text-xs text-muted-foreground">{t('common.activeLanguageFilter')}</span>
              {selectedLanguages.map((lang) => (
                <Badge key={lang.code} variant="secondary" className="text-xs">
                  {lang.flag} {lang.name}
                  <button
                    onClick={() => handleToggleLanguage(lang.code)}
                    className="ml-1.5 hover:text-destructive"
                    aria-label={`Remove ${lang.name} filter`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {onClear && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClear}
                  className="text-xs h-7"
                >
                  {t('filters.clearAll')}
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

